import {campaignPlan} from '@algoquest/shared/campaign/challenge';
import type { LevelDefinition } from '@algoquest/schemas';
import { campaign } from '../../levels/campaign';
import { loadLevel } from '../../levels/levelLoader';
import { ListLevelsResponseSchema, ListProgressResponseSchema, SubmitAttemptEventsResponseSchema, SubmitAttemptResponseSchema, UpsertLevelResponseSchema } from '@algoquest/contracts';
import { apiRequest } from './httpClient';
import { withAuthRetry } from './authClient';
import { cacheProgressRow, cacheProgress, listOutboxAttempts, listOutboxEvents, removeOutboxAttempt, removeOutboxEvents } from '../db/localDb';

export class CampaignDefinitionMismatch extends Error {
  constructor(number: number) {
    super(`Campaign level ${number} differs from the server catalog. Your result is kept on this device. Ask an administrator to update the server campaign.`);
    this.name = 'CampaignDefinitionMismatch';
  }
}

/** Registration never overwrites authored server content. Check the returned
 * definition before associating an attempt with a possibly old prototype row.
 */
export async function upsertLevelDefinition(definition: LevelDefinition): Promise<void> {
  const response = await withAuthRetry(() => apiRequest('/levels', { method: 'POST', body: { definition } }, UpsertLevelResponseSchema));
  const serverDefinition = loadLevel(response.level.definition).definition;
  if (JSON.stringify(serverDefinition) !== JSON.stringify(loadLevel(definition).definition)) {
    throw new CampaignDefinitionMismatch(definition.coordinates.globalLevelNumber);
  }
}

/** Register each pending attempt's own definition, including after an offline
 * world switch. Never infer the definition from the level currently on screen.
 */
export async function drainOutbox(): Promise<void> {
  const pending = (await listOutboxAttempts()).sort((a,b)=>a.globalLevelNumber-b.globalLevelNumber);
  const registered = new Set<string>();
  for (const record of pending) {
    const { levelDefinition, ...attempt } = record;
    if (!levelDefinition || levelDefinition.coordinates.globalLevelNumber !== attempt.globalLevelNumber || (!attempt.phaseResults && levelDefinition.algorithm !== attempt.algorithm)) {
      throw new Error('Pending attempt has no matching campaign definition; it has been retained locally.');
    }
    if(attempt.phaseResults)campaignPlan(levelDefinition,attempt.algorithm);
    const key = JSON.stringify(levelDefinition);
    if (!registered.has(key)) { await upsertLevelDefinition(levelDefinition); registered.add(key); }
    const response = await withAuthRetry(() => apiRequest('/attempts', { method: 'POST', body: attempt }, SubmitAttemptResponseSchema));
    await cacheProgressRow(response.progress);
    await removeOutboxAttempt(attempt.attemptId);
  }
  for (const record of await listOutboxEvents()) {
    await withAuthRetry(() => apiRequest('/attempt-events', { method: 'POST', body: record }, SubmitAttemptEventsResponseSchema));
    await removeOutboxEvents(record.attemptId);
  }
}
export async function pullProgress(): Promise<void> {
  const [response, catalog] = await Promise.all([
    withAuthRetry(() => apiRequest('/progress', { method: 'GET' }, ListProgressResponseSchema)),
    withAuthRetry(() => apiRequest('/levels', { method: 'GET' }, ListLevelsResponseSchema)),
  ]);
  // Historic prototype rows reused official global numbers. Only count progress
  // whose server definition actually matches this campaign; never delete history.
  const matchingIds = new Map<string, number>();
  for (const row of catalog.levels) {
    const local = campaign.find(item => item.definition.coordinates.globalLevelNumber === row.globalLevelNumber);
    if (local && JSON.stringify(loadLevel(row.definition).definition) === JSON.stringify(local.definition)) {
      matchingIds.set(row.id, row.globalLevelNumber);
    }
  }
  await cacheProgress(response.progress.filter(row => matchingIds.get(row.levelId) === row.globalLevelNumber).map(row => ({
    globalLevelNumber: row.globalLevelNumber, completed: row.completed, mastered: row.mastered,
    bestStars: row.bestStars, bestDurationMs: row.bestDurationMs, attemptsCount: row.attemptsCount,
  })));
}

// Coalesce overlapping boot/completion/retry calls; do not submit one queue twice.
let syncing: Promise<void> | null = null;
let syncRequested = false;
let suspended=false;
export async function suspendSync():Promise<void>{suspended=true;syncRequested=false;await syncing?.catch(()=>{});}
export function resumeSync():void{suspended=false;}
export function runFullSync(): Promise<void> {
  if(suspended)return Promise.resolve();
  syncRequested = true;
  if (!syncing) syncing = (async () => {
    do { syncRequested = false; await drainOutbox(); await pullProgress(); } while (syncRequested&&!suspended);
  })().finally(() => { syncing = null; });
  return syncing;
}
